import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { orderService } from '@/services/order';
import type { Order } from '@/types';
import { getEffectiveOrderTime } from '@/utils/orderTime';
import { reviewService } from '@/services/review';
import { StarRatingInput } from '@/components/review/StarRatingInput';
import { ReviewTextInput } from '@/components/review/ReviewTextInput';
import { PhotoUploadSection } from '@/components/review/PhotoUploadSection';
import { ReviewSubmitButton } from '@/components/review/ReviewSubmitButton';
import { useToast } from '@/components/common/Toast';

export function ReviewWritePage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!orderId) {
      showToast('잘못된 접근입니다');
      navigate('/buyer/orders');
      return;
    }

    fetchOrderData();
  }, [user, orderId, navigate]);

  const fetchOrderData = async () => {
    if (!user || !orderId) return;

    setIsLoading(true);
    try {
      const orderData = await orderService.getOrderById(orderId);
      
      if (!orderData) {
        showToast('주문을 찾을 수 없습니다');
        navigate('/buyer/orders');
        return;
      }

      // 주문 완료된 상태인지 확인
      if (orderData.status !== 'COMPLETED') {
        showToast('완료된 주문만 리뷰 작성이 가능합니다');
        navigate('/buyer/orders');
        return;
      }

      // 이미 리뷰를 작성했는지 확인
      const existingReview = await reviewService.getReviewByOrderId(orderId, user.id);
      if (existingReview) {
        showToast('이미 리뷰를 작성한 주문입니다');
        navigate('/buyer/orders');
        return;
      }

      setOrder(orderData);
    } catch (error) {
      console.error('Error fetching order:', error);
      showToast('주문 정보를 불러올 수 없습니다');
      navigate('/buyer/orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user || !order || !orderId) return;

    if (rating < 0.5) {
      showToast('별점을 선택해주세요');
      return;
    }

    if (comment.length < 5) {
      showToast('리뷰를 5자 이상 작성해주세요');
      return;
    }

    try {
      const reviewData = {
        restaurant_id: order.restaurant_id,
        order_id: orderId,
        rating: rating, // UI 0.5-5.0 값을 서비스에서 1-10으로 변환
        comment,
        is_anonymous: isAnonymous,
        is_verified: true,
      };

      const result = await reviewService.createReview(user.id, reviewData);
      
      if (result.success && result.review) {
        // 사진이 있으면 업로드
        if (photos.length > 0) {
          await reviewService.uploadReviewImages(result.review.id, photos);
        }

        showToast('리뷰가 성공적으로 등록되었습니다');
        navigate('/buyer/orders');
      } else {
        showToast(result.error || '리뷰 등록에 실패했습니다');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      showToast('리뷰 등록 중 오류가 발생했습니다');
    }
  };

  const isFormValid = rating >= 0.5 && comment.length >= 5 && comment.length <= 200;

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">주문 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex items-center justify-center px-4 py-3 relative">
          <button
            onClick={() => navigate('/buyer/orders')}
            className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              width="24"
              height="24"
            />
          </button>
          <h1 className="text-2xl font-bold">리뷰 작성</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-8">
        {/* Order Info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h2 className="text-xl font-semibold mb-2">{order.restaurant?.name}</h2>
          <div className="text-sm text-gray-600 space-y-1">
            <p>주문일시: {new Date(getEffectiveOrderTime(order) || order.created_at).toLocaleDateString('ko-KR')}</p>
            <p>주문금액: {order.total_amount.toLocaleString()}원</p>
            {order.items && order.items.length > 0 && (
              <p>주문상품: {order.items[0].product?.name}
                {order.items.length > 1 && ` 외 ${order.items.length - 1}개`}
              </p>
            )}
          </div>
        </div>

        {/* Rating Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">음식은 어떠셨나요?</h3>
          <div className="flex justify-center">
            <StarRatingInput 
              rating={rating} 
              onRatingChange={setRating} 
            />
          </div>
        </div>

        {/* Review Text Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">리뷰를 작성해주세요</h3>
          <ReviewTextInput 
            value={comment}
            onChange={setComment}
            minLength={5}
            maxLength={200}
          />
        </div>

        {/* Photo Upload Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">사진 첨부 (선택사항)</h3>
          <PhotoUploadSection 
            photos={photos}
            onPhotosChange={setPhotos}
            maxPhotos={3}
          />
        </div>

        {/* Anonymous Option */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="anonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="anonymous" className="text-base font-medium cursor-pointer">
              익명으로 리뷰 작성하기
            </label>
          </div>
          <p className="text-sm text-gray-500">
            익명 리뷰로 작성하면 다른 사용자에게 닉네임이 표시되지 않습니다.
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <ReviewSubmitButton 
            onSubmit={handleSubmit}
            isValid={isFormValid}
          />
        </div>

        {/* Guidelines */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
          <h4 className="font-semibold text-blue-900">리뷰 작성 가이드라인</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 실제 경험을 바탕으로 정직하게 작성해주세요</li>
            <li>• 욕설, 비방, 개인정보는 포함하지 말아주세요</li>
            <li>• 음식의 맛, 양, 서비스 등을 구체적으로 설명해주세요</li>
            <li>• 다른 고객들에게 도움이 되는 리뷰를 부탁드립니다</li>
          </ul>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
